import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Modal,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GoogleIcon from "@mui/icons-material/Google";
import {
  signInWithGoogle,
  signInWithEmail,
  auth,
} from "../../Firebase/Firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";

// Admin emails list
const adminEmails = [
  "admin1@example.com",
  "admin2@example.com",
  "testadmin@gmail.com",
  "ronnyrajput.com@gmail.com",
];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
};

const Login = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true); // modal open by default
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSignup, setIsSignup] = useState(false);

  const handleClose = () => setOpen(false);

  // Function to check admin
  const isAdmin = (email) => adminEmails.includes(email);

  // Google Login
  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithGoogle();
      const userEmail = result.user.email;

      if (isAdmin(userEmail)) {
        navigate("/dashboard");
      } else {
        setError("Access denied! Only admin can access the dashboard.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Email/Password Login or Signup
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    try {
      if (isSignup) {
        await createUserWithEmailAndPassword(auth, email, password);
        setError("Signup successful! Please login now.");
        setIsSignup(false);
      } else {
        const userCred = await signInWithEmail(email, password);
        const userEmail = userCred.user.email;

        if (!isAdmin(userEmail)) {
          setError("Access denied! Only admin can access the dashboard.");
          return;
        }

        navigate("/dashboard");
      }
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        setError("User not found! Please sign up first.");
      } else if (err.code === "auth/wrong-password") {
        setError("Invalid password. Try again.");
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" align="center" gutterBottom>
          {isSignup ? "Sign Up" : "Login"}
        </Typography>

        {error && (
          <Typography color="error" variant="body2" align="center" mb={2}>
            {error}
          </Typography>
        )}

        {/* Google Login on top */}
        <Button
          onClick={handleGoogleLogin}
          variant="contained"
          color="secondary"
          fullWidth
          startIcon={<GoogleIcon />}
          sx={{ mb: 2 }}
        >
          Continue with Google
        </Button>

        <Divider sx={{ my: 2 }}>OR</Divider>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailAuth}>
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
          >
            {isSignup ? "Sign Up" : "Login with Email"}
          </Button>
        </form>

        <Button
          onClick={() => {
            setIsSignup(!isSignup);
            setError("");
          }}
          fullWidth
          sx={{ mt: 1 }}
        >
          {isSignup ? "Already have an account? Login" : "New user? Sign Up"}
        </Button>
      </Box>
    </Modal>
  );
};

export default Login;
